import { useState, useEffect, createContext, useContext } from 'react';

// Theme definitions - Comfort & Premium Design
export const themes = {
  // Sunshine Theme - Warm, premium feel
  sunshine: {
    name: 'Sunshine',
    key: 'sunshine',
    background: '#FFFBF5',           // Warm cream white
    backgroundAlt: '#FFF7ED',         // Soft orange tint
    backgroundWarm: '#FEF3C7',        // Golden warm
    textPrimary: '#1C1917',           // Warm dark brown
    textSecondary: '#44403C',         // Warm gray
    textMuted: '#78716C',             // Stone muted
    primary: '#F59E0B',               // Warm amber/gold
    primaryHover: '#D97706',          // Darker amber
    secondary: '#EA580C',             // Warm orange
    secondaryHover: '#C2410C',        // Deep orange
    accent: '#F472B6',                // Soft pink
    cardBg: '#FFFFFF',
    cardBorder: '#FED7AA',            // Peach border
    inputBg: '#FFFFFF',
    inputBorder: '#FDBA74',           // Orange border
    headerBg: 'rgba(255, 251, 245, 0.95)',
    footerBg: '#292524',              // Warm dark
    footerText: '#FAFAF9',
  },
  // Ocean Theme - Professional blue
  ocean: {
    name: 'Ocean',
    key: 'ocean',
    background: '#F0F9FF',            // Light sky blue
    backgroundAlt: '#E0F2FE',         // Soft blue
    backgroundWarm: '#DBEAFE',        // Light blue warm
    textPrimary: '#0C4A6E',           // Deep ocean blue
    textSecondary: '#0369A1',         // Ocean blue
    textMuted: '#0284C7',             // Sky blue muted
    primary: '#FBBF24',               // Golden yellow
    primaryHover: '#F59E0B',          // Amber
    secondary: '#0EA5E9',             // Sky blue
    secondaryHover: '#0284C7',        // Darker sky
    accent: '#F472B6',                // Pink accent
    cardBg: '#FFFFFF',
    cardBorder: '#BAE6FD',            // Light blue border
    inputBg: '#FFFFFF',
    inputBorder: '#7DD3FC',           // Sky border
    headerBg: 'rgba(240, 249, 255, 0.95)',
    footerBg: '#0C4A6E',              // Deep ocean
    footerText: '#F0F9FF',
  },
};

// Theme order for display
export const themeOrder = ['sunshine', 'ocean'];

// Theme Context
const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('ocean'); // Default to Ocean

  useEffect(() => {
    // Load saved theme from localStorage
    const savedTheme = localStorage.getItem('teacherHappyTheme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
    // If no saved theme, default stays as 'ocean'
  }, []);

  useEffect(() => {
    // Apply theme CSS variables to document
    const theme = themes[currentTheme];
    const root = document.documentElement;
    
    root.style.setProperty('--th-background', theme.background);
    root.style.setProperty('--th-background-alt', theme.backgroundAlt);
    root.style.setProperty('--th-background-warm', theme.backgroundWarm);
    root.style.setProperty('--th-text-primary', theme.textPrimary);
    root.style.setProperty('--th-text-secondary', theme.textSecondary);
    root.style.setProperty('--th-text-muted', theme.textMuted);
    root.style.setProperty('--th-primary', theme.primary);
    root.style.setProperty('--th-primary-hover', theme.primaryHover);
    root.style.setProperty('--th-secondary', theme.secondary);
    root.style.setProperty('--th-secondary-hover', theme.secondaryHover);
    root.style.setProperty('--th-accent', theme.accent);
    root.style.setProperty('--th-card-bg', theme.cardBg);
    root.style.setProperty('--th-card-border', theme.cardBorder);
    root.style.setProperty('--th-input-bg', theme.inputBg);
    root.style.setProperty('--th-input-border', theme.inputBorder);
    root.style.setProperty('--th-header-bg', theme.headerBg);
    root.style.setProperty('--th-footer-bg', theme.footerBg);
    root.style.setProperty('--th-footer-text', theme.footerText);
    
    // Save to localStorage
    localStorage.setItem('teacherHappyTheme', currentTheme);
  }, [currentTheme]);

  const switchTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      theme: themes[currentTheme], 
      switchTheme,
      themes,
      themeOrder
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
